import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChartLine, FaBrain, FaRegListAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

// -------------------- Section Styles --------------------

const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" }
  })
};

const FeaturesContainer = styled.section`
  padding: 6rem 10%;
  background: linear-gradient(
    135deg,
    #0f1322,
    #13192d,
    #1b2340
  );
  background-size: 200% 200%;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  margin-bottom: 1rem;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #b8bfd6;
  margin-bottom: 4rem;
  opacity: 0.9;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.2rem;
`;

// -------------------- Card Styles --------------------

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  padding: 2.7rem 2rem;
  border-radius: 16px;
  transition: 0.4s ease;
  cursor: default;

  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: rgba(93, 115, 255, 0.6);
    box-shadow: 0 15px 40px rgba(80, 120, 255, 0.25);
  }
`;

const IconWrapper = styled.div`
  font-size: 3.2rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
  filter: drop-shadow(0px 0px 10px rgba(80, 120, 255, 0.25));
`;

const FeatureTitle = styled.h3`
  font-size: 1.45rem;
  margin-bottom: 0.7rem;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.3px;
`;

const FeatureDescription = styled.p`
  color: #c4c8dd;
  line-height: 1.65;
  font-size: 0.98rem;
  max-width: 90%;
  margin: 0 auto;
`;

// -------------------- Data --------------------

const featuresData = [
  {
    icon: <FaChartLine />,
    title: "Advanced Charting Tools",
    description:
      "Analyze trends with precision using real-time charting tools designed for both beginners and expert traders."
  },
  {
    icon: <FaBrain />,
    title: "AI-Powered Insights",
    description:
      "Unlock predictive analytics with AI models that scan market conditions and identify opportunities instantly."
  },
  {
    icon: <FaRegListAlt />,
    title: "Web3 Social",
    description:
      "Trend Genie Social is where the ecosystem comes alive. It is a social platform with gamified engagement and reward mechanics designed to keep users active daily."
  }
];

// -------------------- Component --------------------

const Features = () => {
  return (
    <FeaturesContainer id="features">
      <SectionTitle>Everything You Need to Succeed</SectionTitle>
      <SectionSubtitle>
        Powerful tools, smarter insights, and complete control over your trading journey.
      </SectionSubtitle>

      <FeaturesGrid>
        {featuresData.map((feature, index) => (
          <FeatureCard
            key={index}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={index}
          >
            <IconWrapper>{feature.icon}</IconWrapper>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>
    </FeaturesContainer>
  );
};

export default Features;
